import {
  faArrowsRotate,
  faCircleInfo,
  faClockRotateLeft,
} from '@fortawesome/pro-regular-svg-icons';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Container } from 'src/components/Container';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import {
  DNS_MIGRATION_GUIDE_URL,
  dnsBlockSnooze,
  dnsBlockVerify,
} from 'src/models/platform/dnsBlock';
import { isDnsBlockStaff } from 'src/models/platform/dnsBlockGuard';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';

const DNS_BLOCK_DEADLINE = 'July 15';

const DnsBlockPage = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'dnsBlock' });
  const { session, user, refreshSessionAndUser } = useSession();
  const queryClient = useQueryClient();
  const location = useLocation();
  const from =
    (location.state as { from?: string } | null)?.from?.replace(/^\/ex4/, '') ||
    '/';

  const activity = session.dnsBlockActivity;
  const canSnooze = (activity?.snoozesRemaining || 0) > 0;

  const supportHref = session.supportEmailAddress
    ? `mailto:${session.supportEmailAddress}`
    : externalLinks.support;

  const verifyMutation = useMutation({
    mutationFn: dnsBlockVerify,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['session'] });
      await refreshSessionAndUser();
      if (data.migrationComplete) {
        toast.success(t('verifySuccess'));
        window.location.replace(`${externalLinks.base}${from}`);
      }
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('verifyError')));
    },
  });

  const snoozeMutation = useMutation({
    mutationFn: dnsBlockSnooze,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['session'] });
      await refreshSessionAndUser();
      toast.success(t('snoozeSuccess'));
      window.location.replace(`${externalLinks.base}${from}`);
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('snoozeError')));
    },
  });

  if (!isDnsBlockStaff(user) || !session.dnsMigrationBlocked) {
    return <Navigate to={from} replace />;
  }

  if (activity?.snoozeActive) {
    return <Navigate to={from} replace />;
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        minHeight: '100vh',
        pt: 10,
        pb: 2,
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: 500 }}>
        <Stack spacing={2}>
          <Stack alignItems="center" direction="row" spacing={0.5}>
            <Icon color="warning" icon={faTriangleExclamation} size="small" />
            <Typography color="text.secondary" variant="caption">
              {t('warningLabel')}
            </Typography>
          </Stack>

          <Stack spacing={2}>
            <Typography component="h1" variant="h5">
              {t('title')}
            </Typography>
            <Typography variant="body2">
              <Trans
                components={{ bold: <strong /> }}
                i18nKey="dnsBlock.description"
                values={{ deadline: DNS_BLOCK_DEADLINE }}
              />
            </Typography>
          </Stack>

          <Card sx={{ p: 2 }} variant="outlined">
            <Typography sx={{ mb: 1 }} variant="subtitle2">
              {t('requiredAction')}
            </Typography>
            <Stack spacing={1}>
              <Typography color="text.secondary" variant="body2">
                {t('requiredActionDescription')}
              </Typography>
              <Link
                href={DNS_MIGRATION_GUIDE_URL}
                rel="noopener noreferrer"
                target="_blank"
              >
                {t('guideLink')}
              </Link>
            </Stack>
          </Card>

          <Stack spacing={2}>
            <Button
              color="primary"
              fullWidth
              loading={verifyMutation.isPending}
              onClick={() => verifyMutation.mutate()}
              size="large"
              startIcon={<Icon icon={faArrowsRotate} />}
              variant="contained"
            >
              {t('verifyChanges')}
            </Button>

            <Stack direction="row" spacing={2}>
              {canSnooze ? (
                <Stack alignItems="center" flex={1} spacing={0.5}>
                  <Button
                    color="neutral"
                    fullWidth
                    loading={snoozeMutation.isPending}
                    onClick={() => snoozeMutation.mutate()}
                    size="medium"
                    startIcon={<Icon icon={faClockRotateLeft} />}
                    variant="tertiary"
                  >
                    {t('snoozeButton')}
                  </Button>
                  <Typography
                    color="text.secondary"
                    textAlign="center"
                    variant="caption"
                  >
                    {t('snoozeCaption', { deadline: DNS_BLOCK_DEADLINE })}
                  </Typography>
                </Stack>
              ) : null}

              <Stack
                alignItems="center"
                flex={1}
                spacing={0.5}
                sx={canSnooze ? undefined : { width: '100%' }}
              >
                <Button
                  color="neutral"
                  component="a"
                  fullWidth
                  href={supportHref}
                  rel="noopener noreferrer"
                  size="medium"
                  startIcon={<Icon icon={faCircleInfo} />}
                  target="_blank"
                  variant="tertiary"
                >
                  {t('contactSupport')}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default DnsBlockPage;
